import $ from 'jquery';

import { isBuilder } from '../utils-utils';
import { getCurrentWindowMode, getEnabledBreakpoints } from '../utils-window';
import {
  isDocumentInFullscreen,
  MULTIVIEW_FULLSCREEN_EXIT_SETTLE_MS,
  processData,
  shouldLockMultiViewProcessing,
} from './utils';

(() => {
  let processDataTimeout: ReturnType<typeof setTimeout>;
  let isInitInvoked = false;
  let fullscreenExitedAt = 0;

  const enabledBreakpoints = getEnabledBreakpoints();

  const clearHiddenOnLoad = () => {
    // Remove data attributes that are used to hide elements on page load.
    enabledBreakpoints.forEach(enabledBreakpoint => {
      if (enabledBreakpoint.baseDevice) {
        return;
      }

      const breakpointName = enabledBreakpoint.name.toLowerCase();

      document.querySelectorAll(`[data-et-mv-hidden-${breakpointName}="true"]`).forEach(element => {
        element.removeAttribute(`data-et-mv-hidden-${breakpointName}`);
      });
    });
  };

  const processCurrentBreakpointData = () => {
    // Fullscreen and the post-exit settle window must not swap content under an active player.
    if (shouldLockMultiViewProcessing(isInitInvoked, fullscreenExitedAt)) {
      return;
    }

    fullscreenExitedAt = 0;

    const currentBreakpoint = getCurrentWindowMode();

    if (isInitInvoked) {
      processData(currentBreakpoint, false);
    } else {
      processData(currentBreakpoint, true);

      clearHiddenOnLoad();

      // Set the flag to true after the first invocation.
      isInitInvoked = true;
    }
  };

  const processCurrentBreakpointDataDebounced = () => {
    // Do not let the 200ms resize debounce preempt the fullscreen-exit settle timeout.
    if (shouldLockMultiViewProcessing(isInitInvoked, fullscreenExitedAt)) {
      return;
    }

    if (processDataTimeout) {
      clearTimeout(processDataTimeout);
    }

    processDataTimeout = setTimeout(processCurrentBreakpointData, 200);
  };

  document.addEventListener('DOMContentLoaded', () => {
    // Process data on screen orientation change
    if (window?.screen?.orientation) {
      window.screen.orientation.addEventListener('change', processCurrentBreakpointDataDebounced);
    } else {
      window.addEventListener('orientationchange', processCurrentBreakpointDataDebounced);
    }

    // Process data on window resize
    window.addEventListener('resize', processCurrentBreakpointDataDebounced);

    // After fullscreen ends, wait out Android teardown before applying the restored breakpoint.
    const fullscreenEvents = ['fullscreenchange', 'webkitfullscreenchange'];

    fullscreenEvents.forEach(eventName => {
      document.addEventListener(eventName, () => {
        if (isDocumentInFullscreen()) {
          fullscreenExitedAt = 0;
          return;
        }

        fullscreenExitedAt = Date.now();

        if (processDataTimeout) {
          clearTimeout(processDataTimeout);
        }

        processDataTimeout = setTimeout(processCurrentBreakpointData, MULTIVIEW_FULLSCREEN_EXIT_SETTLE_MS);
      });
    });

    // Process data on paginated content change
    window.addEventListener('divi:setPaginatedContent', processCurrentBreakpointDataDebounced);

    // Process data when group carousel initializes (after clones are created).
    // Only register on frontend to match carousel dispatch logic.
    if (!isBuilder) {
      window.addEventListener('divi:groupCarousel:init', processCurrentBreakpointDataDebounced);
    }

    // Process data on page load
    $(document).on('et_pb_after_init_modules', processCurrentBreakpointData);
  });
})();
